// Auth layer (phase 2 brief, item 1). Amalgamesh is meant to be released
// publicly and must work against ANY OIDC provider (Keycloak, Authelia,
// Google, or an operator's own Authentik instance) — nothing here names a specific
// provider. Local accounts + TOTP are the baseline that always works; OIDC
// is a peer provider that is OFF BY DEFAULT and must never be offered when
// the backend hasn't configured one (the standing rule: never offer what
// isn't there). Three real states this has to render correctly: local
// only, OIDC only, and both.
//
// CONTRACT (read from the backend's own /openapi.json on 2026-09-20, once
// its auth routes landed — this replaces an earlier, pre-backend guess that
// assumed a bearer token; the real backend uses cookie sessions instead):
//
//   - GET  /api/auth/me    -> 200 MeResponse {kind, id, role, label} | 401 | 404
//       No token anywhere in this contract — the session is a cookie the
//       browser carries automatically (same-origin, including through the
//       Vite dev proxy), so there is nothing for this layer to store or
//       attach as an Authorization header. A 404 at boot means this
//       backend build has no auth at all -> status "not_required": the app
//       opens straight to the live view. A 401 means auth exists and is
//       required. A network/5xx failure at boot is treated the same as
//       "not_required" rather than trapping the user behind a login screen
//       they can't reach a server to validate against.
//   - POST /api/auth/login       {username, password}
//         -> 200 LoginResponse: either {totp_required:true, mfa_token} (a
//            second step is required) or {kind,id,role,label} (done, cookie
//            set) | 401 on bad credentials
//   - POST /api/auth/login/totp  {mfa_token, code}
//         -> 200 MeResponse (done, cookie set) | 401 on bad/expired code
//   - POST /api/auth/setup       {username, password} -> 200 MeResponse
//         Bootstraps the first admin account when no users exist yet.
//   - POST /api/auth/logout      -> best-effort; errors are swallowed since
//         the client-side state is cleared regardless (the cookie is
//         cleared server-side).
//   - GET  /api/auth/oidc/login  -> redirects the browser to the configured
//         OIDC provider (a plain link works — the browser follows it).
//   - GET  /api/auth/oidc/callback?code=&state= -> a BACKEND route (the
//         provider's redirect_uri), not a frontend one: the provider
//         redirects the browser here directly, the backend exchanges the
//         code, sets the session cookie, and sends the browser on to the
//         app. This app's own #/auth-callback route (AuthCallback.tsx) is
//         therefore a defensive fallback, not the primary path — it just
//         re-checks GET /api/auth/me, which is all that's needed once the
//         cookie is set.
//   - GET  /api/auth/providers   -> shape not pinned by a Pydantic model in
//         the OpenAPI schema (plain `object`), so this is parsed leniently
//         (see parseSsoInfo) and defaults to "no SSO" if the shape doesn't
//         match what's expected — never guesses a button into existing.
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { apiGetWithBody, apiPost, ApiError, setUnauthorizedHandler } from "../api/client";
import type { AuthUser, LoginResponse, Role } from "../api/types";
import { clearPersistedQueryCache } from "../lib/queryClient";

export type AuthStatus = "checking" | "not_required" | "setup_required" | "unauthenticated" | "authenticated" | "expired";

export interface SsoInfo {
  available: boolean;
  // Generic, provider-neutral default. A real backend supplies its own
  // label here (an Authentik instance would send "Sign in with Authentik") —
  // nothing provider-specific is hardcoded on this side.
  label: string;
  loginPath: string;
}

const SSO_UNAVAILABLE: SsoInfo = { available: false, label: "Sign in with SSO", loginPath: "/api/auth/oidc/login" };

// Lenient on purpose: /api/auth/providers has no pinned schema, so this
// checks a handful of plausible shapes for "is OIDC on, and what should the
// button say" rather than committing to one guess. Falls back to hidden
// (SSO_UNAVAILABLE) on anything it doesn't recognize — the safe default per
// the standing "never offer what isn't there" rule.
function parseSsoInfo(body: unknown): SsoInfo {
  if (!body || typeof body !== "object") return SSO_UNAVAILABLE;
  const b = body as Record<string, unknown>;
  const oidc = b.oidc;
  if (oidc && typeof oidc === "object") {
    const o = oidc as Record<string, unknown>;
    const on = o.enabled === true || o.available === true || o.configured === true;
    if (on) {
      const label = [o.label, o.button_label, o.display_name].find((x): x is string => typeof x === "string" && x.trim().length > 0);
      const path = [o.login_path, o.login_url, o.url].find((x): x is string => typeof x === "string" && x.trim().length > 0);
      return { available: true, label: label ?? SSO_UNAVAILABLE.label, loginPath: path ?? SSO_UNAVAILABLE.loginPath };
    }
    return SSO_UNAVAILABLE;
  }
  if (b.oidc === true) return SSO_UNAVAILABLE; // enabled flag with no label/path metadata isn't enough to show a button confidently
  if (Array.isArray(b.providers) && b.providers.map(String).includes("oidc")) {
    const label = typeof b.oidc_label === "string" ? b.oidc_label : undefined;
    return { available: true, label: label ?? SSO_UNAVAILABLE.label, loginPath: SSO_UNAVAILABLE.loginPath };
  }
  return SSO_UNAVAILABLE;
}

export interface LoginOutcome {
  ok: boolean;
  totpRequired?: boolean;
  pendingToken?: string;
  error?: string;
}

interface AuthValue {
  status: AuthStatus;
  user: AuthUser | null;
  isAdmin: boolean;
  // Bumped on every login/logout/expiry so the WebSocket (whose auth is the
  // same session cookie, carried automatically at connect time) knows to
  // open a fresh connection rather than keep reusing one opened before the
  // session existed.
  authVersion: number;
  login: (username: string, password: string) => Promise<LoginOutcome>;
  submitTotp: (mfaToken: string, code: string) => Promise<LoginOutcome>;
  setupAdmin: (username: string, password: string) => Promise<LoginOutcome>;
  logout: () => Promise<void>;
  // Called whenever *something* saw a 401 or an auth-flavored WS close —
  // never trusted on its own (see handlePossibleAuthFailure's header
  // comment: a real bug found live, 2026-09-20, was exactly "any single 401
  // anywhere instantly means expired"). Re-verifies against /api/auth/me
  // before changing anything.
  handlePossibleAuthFailure: () => void;
  // An honest, specific reason for the current unauthenticated/expired
  // state — set only by handlePossibleAuthFailure's verified outcome, never
  // a generic "session expired" when that isn't actually what happened
  // (e.g. a login that succeeded server-side but whose session cookie the
  // browser never stored, which looks identical to a fast expiry unless
  // this distinguishes them).
  notice: string | null;
  sso: SsoInfo;
  // The real minimum password length POST /api/auth/setup is about to
  // enforce — read from GET /api/auth/me's own setup_required response,
  // never guessed or hardcoded here. Null until that response has been
  // seen (setup screen just doesn't show a length hint yet, rather than a
  // wrong one).
  passwordMinLength: number | null;
}

const Ctx = createContext<AuthValue | null>(null);

function userFromMe(body: { kind?: string | null; id?: string | null; role?: string | null; label?: string | null } | null): AuthUser | null {
  if (!body || (body.role !== "admin" && body.role !== "viewer")) return null;
  return { kind: body.kind ?? "local", id: body.id ?? "", role: body.role, label: body.label ?? body.id ?? "" };
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [status, setStatus] = useState<AuthStatus>("checking");
  const [user, setUser] = useState<AuthUser | null>(null);
  const [authVersion, setAuthVersion] = useState(0);
  const [passwordMinLength, setPasswordMinLength] = useState<number | null>(null);
  const [sso, setSso] = useState<SsoInfo>(SSO_UNAVAILABLE);
  const [notice, setNotice] = useState<string | null>(null);

  // Whether THIS session has ever had a confirmed, live authenticated state
  // (a successful login/totp/setup, or a successful /api/auth/me). Used to
  // pick an honest failure message — see handlePossibleAuthFailure.
  const everConfirmedRef = useRef(false);
  // Debounce: /api/health, /api/stats, /api/features all poll independently
  // and the WS connects on the same mount, so a real auth failure can
  // arrive as 3-4 near-simultaneous signals. Only one verification pass
  // runs at a time.
  const verifyingRef = useRef(false);
  // Identity ("kind:id") of whoever this tab last confirmed authenticated
  // as — data-layer slice, 2026-09-30. Compared on every applySession so a
  // DIFFERENT user logging in in the same browser (same machine, someone
  // else's account, or an admin switching accounts) wipes the previous
  // user's persisted global-data cache (lib/queryClient.ts) before the new
  // session's own fetches populate it — otherwise the new user's very
  // first paint would show the previous user's last-known nodes/packets/
  // stats, which is exactly the "wrong data shown as current" failure mode
  // the persistence layer exists to avoid, not produce. Null until the
  // first successful session in this tab; a same-user re-auth (token
  // refresh, page reload) is not a change and must not wipe a cache that's
  // still valid for the SAME identity.
  const lastIdentityRef = useRef<string | null>(null);

  const applySession = useCallback((body: { kind?: string | null; id?: string | null; role?: string | null; label?: string | null }) => {
    const u = userFromMe(body);
    if (!u) return false;
    const identity = `${u.kind}:${u.id}`;
    if (lastIdentityRef.current !== null && lastIdentityRef.current !== identity) {
      clearPersistedQueryCache();
    }
    lastIdentityRef.current = identity;
    everConfirmedRef.current = true;
    setUser(u);
    setStatus("authenticated");
    setNotice(null);
    setAuthVersion((v) => v + 1);
    return true;
  }, []);

  const clearSession = useCallback((nextStatus: AuthStatus, noticeText: string | null = null) => {
    setUser(null);
    setStatus(nextStatus);
    setNotice(noticeText);
    setAuthVersion((v) => v + 1);
  }, []);

  // THE FIX (real bug found live, 2026-09-20): this used to be a bare
  // `markExpired()` called unconditionally on ANY 401 from ANY request, or
  // on ANY auth-flavored WebSocket close code. That conflated three very
  // different situations into one wrong message — "your session expired" —
  // and could boot a genuinely valid session:
  //   1. A real expiry (was authenticated, cookie died/was revoked) — the
  //      only case "session expired" is actually true.
  //   2. A login that succeeded on the backend but whose session cookie the
  //      browser never stored or never sent back (a proxy/origin/cookie-
  //      attribute problem, not an expiry) — this looks identical to (1) a
  //      few hundred ms later if nothing re-checks.
  //   3. The WebSocket's own auth check failing/mismatching while the real
  //      HTTP session is still perfectly valid (its cookie handling could
  //      differ from a plain fetch's) — this must never log a valid user
  //      out on its own.
  // The fix: never trust a single 401/close code by itself. Re-verify
  // against GET /api/auth/me (the one endpoint whose entire purpose is
  // "am I logged in") and act on THAT authoritative answer, with a message
  // that reflects what was actually observed.
  const handlePossibleAuthFailure = useCallback(() => {
    if (verifyingRef.current) return;
    verifyingRef.current = true;
    (async () => {
      try {
        const { status: httpStatus, body } = await apiGetWithBody<{ kind?: string; id?: string; role?: string; label?: string }>("/api/auth/me");
        if (httpStatus >= 200 && httpStatus < 300 && body) {
          // The real session is fine — whatever triggered this (a WS auth
          // mismatch, a transient blip on one poll) was a false alarm.
          // Re-apply it (cheap, idempotent) rather than leave stale state.
          applySession(body);
          return;
        }
        if (httpStatus === 401) {
          if (everConfirmedRef.current) {
            clearSession("expired", "Your session expired. Sign in again to continue.");
          } else {
            // We were never actually confirmed authenticated in this tab —
            // most likely the login itself succeeded server-side but the
            // browser didn't keep the session cookie (a proxy/origin
            // mismatch, or a cookie attribute the browser refused over this
            // connection), not a real expiry. Say so plainly rather than
            // claiming a session existed and expired.
            clearSession(
              "unauthenticated",
              "Signed in, but the browser didn't keep the session afterward. This usually means the session cookie wasn't stored or wasn't sent back — check you're using the exact address the app expects, and that cookies aren't being blocked for this site.",
            );
          }
          return;
        }
        // Network error (status 0) or an unexpected 5xx: don't change
        // anything — this is "the backend is unreachable right now," which
        // BackendDownBanner already communicates, not an auth verdict.
      } finally {
        verifyingRef.current = false;
      }
    })();
  }, [applySession, clearSession]);

  // Global 401 handler for every plain fetch (see api/client.ts); the
  // WebSocket layer calls the same function on an auth-flavored close code
  // (wired in AppData.tsx, which owns the socket). Neither trusts its own
  // signal directly anymore — see handlePossibleAuthFailure above.
  useEffect(() => {
    setUnauthorizedHandler(() => handlePossibleAuthFailure());
    return () => setUnauthorizedHandler(null);
  }, [handlePossibleAuthFailure]);

  // Fetch SSO/provider info independently of the me-check below, from the
  // one endpoint that's actually meant to answer it, rather than piggy-
  // backing on /api/auth/me's body — separated once the real endpoint's
  // existence was confirmed via the backend's own OpenAPI schema.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { status: httpStatus, body } = await apiGetWithBody<unknown>("/api/auth/providers");
      if (cancelled) return;
      if (httpStatus >= 200 && httpStatus < 300) setSso(parseSsoInfo(body));
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Boot check: is there a live session already (cookie), is auth even on
  // for this backend, and — if not logged in — does the backend say no
  // admin account exists yet (setup_required)?
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { status: httpStatus, body } = await apiGetWithBody<{
        kind?: string;
        id?: string;
        role?: string;
        label?: string;
        setup_required?: boolean;
        password_min_length?: number;
      }>("/api/auth/me");
      if (cancelled) return;

      if (httpStatus === 404) {
        setStatus("not_required");
        return;
      }
      if (httpStatus === 401) {
        if (body && (body as { setup_required?: unknown }).setup_required === true) {
          const minLen = (body as { password_min_length?: unknown }).password_min_length;
          if (typeof minLen === "number") setPasswordMinLength(minLen);
          setStatus("setup_required");
        } else {
          setStatus("unauthenticated");
        }
        return;
      }
      if (httpStatus >= 200 && httpStatus < 300 && body) {
        if (!applySession(body)) setStatus("unauthenticated");
        return;
      }
      // Network error (status 0) or an unexpected 5xx at boot: don't trap
      // the user behind a login wall they can't reach a server to validate
      // against — BackendDownBanner already covers "backend is unreachable"
      // for the rest of the app.
      setStatus("not_required");
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = useCallback(
    async (username: string, password: string): Promise<LoginOutcome> => {
      try {
        const res = await apiPost<LoginResponse>("/api/auth/login", { username, password });
        if (res?.totp_required) {
          return { ok: false, totpRequired: true, pendingToken: res.mfa_token ?? undefined };
        }
        if (applySession(res)) return { ok: true };
        return { ok: false, error: "Unexpected response while logging in." };
      } catch (e) {
        if (e instanceof ApiError && e.status === 401) return { ok: false, error: "Incorrect username or password." };
        if (e instanceof ApiError && e.status === 404) {
          return { ok: false, error: "Local login isn't available on this backend build yet." };
        }
        return { ok: false, error: e instanceof Error ? e.message : "Login failed." };
      }
    },
    [applySession],
  );

  const submitTotp = useCallback(
    async (mfaToken: string, code: string): Promise<LoginOutcome> => {
      try {
        const res = await apiPost<{ kind?: string; id?: string; role?: string; label?: string }>("/api/auth/login/totp", { mfa_token: mfaToken, code });
        if (applySession(res)) return { ok: true };
        return { ok: false, error: "Unexpected response during verification." };
      } catch (e) {
        if (e instanceof ApiError && e.status === 401) return { ok: false, error: "Incorrect or expired code." };
        return { ok: false, error: e instanceof Error ? e.message : "Verification failed." };
      }
    },
    [applySession],
  );

  const setupAdmin = useCallback(
    async (username: string, password: string): Promise<LoginOutcome> => {
      try {
        const res = await apiPost<{ kind?: string; id?: string; role?: string; label?: string }>("/api/auth/setup", { username, password });
        if (applySession(res)) return { ok: true };
        return { ok: false, error: "Unexpected response during setup." };
      } catch (e) {
        return { ok: false, error: e instanceof Error ? e.message : "Setup failed." };
      }
    },
    [applySession],
  );

  const logout = useCallback(async () => {
    try {
      await apiPost("/api/auth/logout");
    } catch {
      /* best-effort — client-side state is cleared regardless */
    }
    everConfirmedRef.current = false;
    lastIdentityRef.current = null;
    // Data-layer slice (2026-09-30): wipe both the in-memory QueryClient
    // cache AND the persisted IndexedDB copy (persister.removeClient()) —
    // in-memory alone would still leave last-known nodes/packets/stats on
    // disk for the NEXT person to use this browser (or this same person,
    // logged out, before ever logging back in) to see flashed on first
    // paint before a fresh fetch lands.
    await clearPersistedQueryCache();
    clearSession("unauthenticated", null);
  }, [clearSession]);

  const value = useMemo<AuthValue>(
    () => ({
      status,
      user,
      isAdmin: user?.role === "admin",
      authVersion,
      login,
      submitTotp,
      setupAdmin,
      logout,
      handlePossibleAuthFailure,
      notice,
      sso,
      passwordMinLength,
    }),
    [status, user, authVersion, login, submitTotp, setupAdmin, logout, handlePossibleAuthFailure, notice, sso, passwordMinLength],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAuth(): AuthValue {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}

// Called from the #/auth-callback route as a defensive fallback (see the
// header comment above — the primary OIDC path never needs this, since the
// backend's own /api/auth/oidc/callback sets the cookie and sends the
// browser onward). All this does is confirm the session actually took by
// re-checking /api/auth/me — there is no token to read from the URL in this
// contract.
export async function reconcileAfterOidcRedirect(): Promise<AuthUser | null> {
  const { status: httpStatus, body } = await apiGetWithBody<{ kind?: string; id?: string; role?: string; label?: string }>("/api/auth/me");
  if (httpStatus >= 200 && httpStatus < 300) return userFromMe(body);
  return null;
}
